import type { ReactNode } from 'react';
import clsx from 'clsx';
import React from 'react';

import type { TextSizeType } from '../../utils';
import { Button } from '../../Button/BaseButton/Button';
import { Cell } from '../Cell';
import { type ColumnDefinition, gridClasses } from '../helpers';

const formatKeyAsText = <T,>(data: T) => <span>{String(data)}</span>;

const format = <T,>(value: T, definition: ColumnDefinition<T>) => {
  if ('format' in definition) {
    return definition.format(value);
  }
  return formatKeyAsText(value[definition.key]);
};

type RowProps<T> = {
  value: T;
  schema: readonly ColumnDefinition<T>[];
  selected?: boolean;
  index: number;
  textSize?: TextSizeType;
  disableHover?: boolean;
  bordered?: boolean;
  isLast?: boolean;
  rowHasNoAffect?: boolean;
  backgroundColor?: string;
};

export const Row = <T,>({
  value,
  schema,
  selected,
  index,
  textSize = 'sm',
  disableHover = false,
  bordered = false,
  isLast = false,
  rowHasNoAffect = false,
  backgroundColor,
}: RowProps<T>) => (
  <div
    className={clsx(
      gridClasses(textSize),
      selected ? 'bg-negative' : backgroundColor ?? 'bg-transparent',
      !disableHover &&
        'group-hover:bg-negative group-focus:bg-negative cursor-pointer',
      bordered && 'border-[0.5px] border-main-300',
      !isLast && 'border-b-0',
      rowHasNoAffect && 'text-main-500'
    )}
    role="row"
    aria-rowindex={index}
  >
    {schema.map((def) => (
      <Cell
        textSize={textSize}
        key={def.title}
        justify={def.justify}
        colspan={def.colspan}
      >
        {format(value, def)}
      </Cell>
    ))}
  </div>
);
Row.displayName = 'NavTable.Row';

type EmptyTableRowProps<T> = {
  value: Record<ColumnDefinition<T>['title'], ReactNode>;
  schema: readonly ColumnDefinition<T>[];
  textSize?: TextSizeType;
  bordered?: boolean;
};

export const EmptyTableRow = <T,>({
  value,
  schema,
  textSize = 'sm',
  bordered = false,
}: EmptyTableRowProps<T>) => (
  <div
    className={clsx(
      gridClasses(textSize),
      'bg-canvas',
      bordered && 'border-[0.5px] border-main-300'
    )}
    role="row"
  >
    {schema.map(({ title, justify, colspan }) => (
      <Cell textSize={textSize} key={title} justify={justify} colspan={colspan}>
        {value[title]}
      </Cell>
    ))}
  </div>
);

export type LoadMoreRowProps = {
  isLoading?: boolean;
  loadMore: VoidFunction;
  textSize?: TextSizeType;
  bordered?: boolean;
};

export const LoadMoreRow = ({
  isLoading,
  loadMore,
  bordered = false,
  textSize = 'sm',
}: LoadMoreRowProps) => (
  <div className={clsx(gridClasses(textSize), 'hover:bg-negative')} role="row">
    <Cell justify="left" textSize={textSize}>
      <Button
        textAlign="text-left"
        type="button"
        disabled={isLoading}
        onClick={loadMore}
        color="text-main-500 hover:text-main"
        borderColor={bordered ? 'border-main-300' : undefined}
        borderWidth={bordered ? 'border-x-[0.5px] border-b-[0.5px]' : undefined}
      >
        {isLoading ? 'Loading...' : 'Load More'}
      </Button>
    </Cell>
  </div>
);
